import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Snackbar,
} from '@mui/material';
import { ToggleOn, Block, Groups, AdminPanelSettings } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { Card, CardHeader, CardContent } from '../../design-system/components';
import colors from '../../design-system/colors';
import shadows from '../../design-system/shadows';
import spacing from '../../design-system/spacing';
import usePageTitle from '../../hooks/usePageTitle';
import useFeatureFlags from '../../hooks/useFeatureFlags';

const FeatureFlagsAdmin = () => {
  const theme = useTheme();
  const { t } = useTranslation(['admin', 'common']);
  const { featureFlags, loading, updateFlag } = useFeatureFlags();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  usePageTitle(t('featureFlags.pageTitle'));

  const features = [
    {
      key: 'licencias',
      name: t('featureFlags.features.licencias.name'),
      description: t('featureFlags.features.licencias.description'),
    },
    {
      key: 'chatIA',
      name: t('featureFlags.features.chatIA.name'),
      description: t('featureFlags.features.chatIA.description'),
    },
    {
      key: 'planificacion',
      name: t('featureFlags.features.planificacion.name'),
      description: t('featureFlags.features.planificacion.description'),
    },
    {
      key: 'promoCodigos',
      name: t('featureFlags.features.promoCodigos.name'),
      description: t('featureFlags.features.promoCodigos.description'),
    },
  ];

  const handleFlagChange = async (featureKey, newState) => {
    try {
      await updateFlag(featureKey, newState);
      setSnackbar({
        open: true,
        message: t('featureFlags.updateSuccess'),
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('featureFlags.updateError'),
        severity: 'error',
      });
    }
  };

  const getStateColor = (state) => {
    switch (state) {
      case 'disabled':
        return theme.palette.text.secondary;
      case 'admin_only':
        return theme.palette.mode === 'dark' ? '#ffd58a' : '#825200';
      case 'everyone':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStateIcon = (state) => {
    switch (state) {
      case 'disabled':
        return <Block />;
      case 'admin_only':
        return <AdminPanelSettings />;
      case 'everyone':
        return <Groups />;
      default:
        return <Block />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('common:loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box className="wp-page" sx={{ p: 3 }}>
      <Box className="wp-heading" sx={{ mb: 4 }}>
        <Typography component="h1" variant="h4" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
          {t('featureFlags.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('featureFlags.subtitle')}
        </Typography>
      </Box>

      <Card>
        <CardHeader
          title={t('featureFlags.cardTitle')}
          icon={<ToggleOn />}
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {t('featureFlags.table.feature')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {t('featureFlags.table.description')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {t('featureFlags.table.state')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {t('featureFlags.table.control')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => {
                  const flag = featureFlags.find(f => f.id === feature.key);
                  const currentState = flag?.state || 'disabled';
                  return (
                    <TableRow key={feature.key}>
                      <TableCell>
                        <Typography fontWeight={600} sx={{ color: 'text.primary' }}>
                          {feature.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 0.5,
                            borderRadius: `${spacing.borderRadius.md}px`,
                            backgroundColor: alpha(getStateColor(currentState), .12),
                            color: getStateColor(currentState),
                          }}
                        >
                          {getStateIcon(currentState)}
                          <Typography variant="body2" fontWeight={600}>
                            {t(`featureFlags.states.${currentState}`)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <ToggleButtonGroup
                          value={currentState}
                          exclusive
                          onChange={(e, value) => {
                            if (value !== null) {
                              handleFlagChange(feature.key, value);
                            }
                          }}
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 2,
                              py: 0.5,
                              fontSize: '0.875rem',
                              textTransform: 'none',
                              border: '1px solid',
                              borderColor: 'divider',
                              '&.Mui-selected': {
                                backgroundColor: colors.primary[50],
                                color: colors.primary[700],
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: colors.primary[100],
                                },
                              },
                            },
                          }}
                        >
                          <ToggleButton value="disabled">
                            {t('featureFlags.controls.disabled')}
                          </ToggleButton>
                          <ToggleButton value="admin_only">
                            {t('featureFlags.controls.adminOnly')}
                          </ToggleButton>
                          <ToggleButton value="everyone">
                            {t('featureFlags.controls.everyone')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mt: 3 }}>
            {t('featureFlags.info')}
          </Alert>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeatureFlagsAdmin;
